import type { StateCreator } from 'zustand'
import type {
  BoqElementQuantityRow,
  BoqInventory,
  BoqSession,
  BoqWriteResult,
} from '@modules/boq/lib/boqClient'

// Lifted out of BoqHomePage so the BoQ session, inventory, results
// and write status survive tab switches inside the workspace layout.
// The page used to keep all of this in `useState` and the layout
// unmounts the BoQ tab when the user clicks another module; every
// switch was wiping the session, forcing a fresh upload + recalc.

export type BoqStage =
  | 'idle'
  | 'opening'
  | 'inventorying'
  | 'calculating'
  | 'done'

export interface BoqSlice {
  boqSession: BoqSession | null
  boqStage: BoqStage
  boqError: string | null
  boqInventory: BoqInventory | null
  boqChosenClasses: Set<string>
  boqProgress: { done: number; total: number }
  boqResults: BoqElementQuantityRow[]
  boqWriteResult: BoqWriteResult | null
  // Object URL of the most recent serialized IFC blob. Lives until
  // the next write or until the user explicitly trocas IFC. We never
  // revoke it on tab switch (the page may want to re-show it).
  boqDownloadHref: string | null
  // Monotonic counter, bumped whenever the BoqSession's internal
  // write log changes (write, undo). Components that render BoQ-
  // written psets (the viewer's PropertiesPanel) read this so they
  // re-render when the log changes, without us having to mirror the
  // log into the store.
  boqWriteVersion: number

  setBoqSession: (s: BoqSession | null) => void
  setBoqStage: (s: BoqStage) => void
  setBoqError: (e: string | null) => void
  setBoqInventory: (inv: BoqInventory | null) => void
  setBoqChosenClasses: (s: Set<string>) => void
  setBoqProgress: (p: { done: number; total: number }) => void
  setBoqResults: (r: BoqElementQuantityRow[]) => void
  patchBoqResults: (rows: BoqElementQuantityRow[]) => void
  setBoqWriteResult: (r: BoqWriteResult | null) => void
  setBoqDownloadHref: (href: string | null) => void
  bumpBoqWriteVersion: () => void
  resetBoq: () => void
}

const INITIAL_STATE = {
  boqSession: null,
  boqStage: 'idle' as BoqStage,
  boqError: null,
  boqInventory: null,
  boqChosenClasses: new Set<string>(),
  boqProgress: { done: 0, total: 0 },
  boqResults: [] as BoqElementQuantityRow[],
  boqWriteResult: null,
  boqDownloadHref: null,
  boqWriteVersion: 0,
}

export const createBoqSlice: StateCreator<BoqSlice> = (set, get) => ({
  ...INITIAL_STATE,

  setBoqSession: (s) => set({ boqSession: s }),
  setBoqStage: (s) => set({ boqStage: s }),
  setBoqError: (e) => set({ boqError: e }),
  setBoqInventory: (inv) => set({ boqInventory: inv }),
  setBoqChosenClasses: (s) => set({ boqChosenClasses: s }),
  setBoqProgress: (p) => set({ boqProgress: p }),
  setBoqResults: (r) => set({ boqResults: r }),

  // Replace any rows whose `guid` matches one in the patch, used
  // after a write to refresh `declared` quantities for just the
  // touched elements without recomputing the full table.
  patchBoqResults: (rows) => {
    const map = new Map(rows.map((r) => [r.guid, r]))
    set({
      boqResults: get().boqResults.map((r) => map.get(r.guid) ?? r),
    })
  },

  setBoqWriteResult: (r) => set({ boqWriteResult: r }),
  setBoqDownloadHref: (href) => set({ boqDownloadHref: href }),
  bumpBoqWriteVersion: () =>
    set((state) => ({ boqWriteVersion: state.boqWriteVersion + 1 })),

  resetBoq: () => {
    const prev = get().boqDownloadHref
    if (prev) {
      try {
        URL.revokeObjectURL(prev)
      } catch {
        /* ignore */
      }
    }
    set({
      ...INITIAL_STATE,
      // INITIAL_STATE.boqChosenClasses is a fresh Set, but we want
      // a NEW Set each reset to avoid sharing the singleton.
      boqChosenClasses: new Set<string>(),
    })
  },
})
